import React, { useContext, useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { ContainerNew } from 'presentation/shared/components/ContainerNew'
import { useHistory, useLocation } from 'react-router'
import HeaderNew from 'presentation/shared/components/HeaderNew'
import SupportTextNew from 'presentation/shared/components/SupportTextNew'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import AutoCompleteNew from 'presentation/shared/components/AutoCompleteNew'
import { ReactComponent as SearchIcon } from 'presentation/assets/icons/search.svg'
import { useServices } from 'presentation/hooks/use-services'
import {
  AmbulatoryAuthorizationDashboardContext,
  Cid
} from 'main/factories/pages/doctor/ambulatory-authorization/ambulatory-authorization-dashboard-factory'
import CardNew from 'presentation/shared/components/CardNew'
import DividerNew from 'presentation/shared/components/DividerNew'
import * as S from './styles'
import HeadingNew from 'presentation/shared/components/HeadingNew'
import { CidCard } from 'presentation/doctor/components/Cards/AmbulatoryCidCard'

type LocationProps = {
  isEditing?: boolean
}

export const AmbulatoryCidsLayout = () => {
  const history = useHistory()
  const stateLocation = useLocation()?.state as LocationProps
  const ambulatoryService = useServices().ambulatory

  const { state, dispatch } = useContext<any>(
    AmbulatoryAuthorizationDashboardContext
  )
  const { patient, cids } = state

  const [cidSearchTerm, setCidSearchTerm] = useState('')
  const [cidsList, setCidsList] = useState<any>([])
  const [selectedCidsList, setSelectedCidsList] = useState<Cid[]>([])
  const [selectedCid, setSelectedCid] = useState<any>(null)

  if (!patient?.cpf) {
    history.push('/pedidos-autorizacao-ambulatorial/solicitacao')
  }

  const formik = useFormik({
    initialValues: {},
    onSubmit: () => {
      addCid()
    }
  })

  function selectCid(cid: any) {
    setCidSearchTerm(cid.name)
    setSelectedCid(cid)
  }

  const clearSelection = () => {
    setCidSearchTerm('')
    setSelectedCid(null)
  }

  async function getCids(searchTerm: string) {
    setCidsList([])
    try {
      const response = await ambulatoryService.getAmbulatoryCids({
        search: searchTerm,
        pageSize: 5,
        pageNumber: 1
      })

      const mappedCidList = response.data.map((item) => {
        return {
          label: item.name,
          value: item.code,
          ...item
        }
      })
      setCidsList(mappedCidList)
    } catch (error: any) {
      toast.error(error?.message)
    }
  }

  function addCid() {
    const isDuplicate = selectedCidsList.some(
      (cid: Cid) => cid.code === selectedCid.code
    )

    if (isDuplicate) {
      toast.error('Este CID já foi adicionado.')
      return
    }

    if (selectedCidsList.length >= 100) {
      toast.error('Não é possível adicionar mais de 100 cids.')
      return
    }

    const list = [...selectedCidsList]

    list.push({
      name: selectedCid.name,
      code: selectedCid.code,
      isMain: list.length === 0
    })

    setSelectedCid(null)
    setSelectedCidsList(list)
    setCidSearchTerm('')
    formik.resetForm()
  }

  function removeCid(code: number) {
    const indexToRemove = selectedCidsList.findIndex(
      (cid: Cid) => cid.code === code
    )

    const updatedList = selectedCidsList.filter((cid: Cid) => cid.code !== code)

    if (selectedCidsList[indexToRemove]?.isMain) {
      if (updatedList[indexToRemove]) {
        updatedList[indexToRemove].isMain = true
      } else if (updatedList.length > 0) {
        updatedList[0].isMain = true
      }
    }

    setSelectedCidsList(updatedList)
  }

  function handleSubmit() {
    dispatch({
      type: 'CIDS',
      payload: selectedCidsList
    })
    if (stateLocation?.isEditing) {
      history.push('resumo#cid')
    } else {
      history.push('medicacao')
    }
  }

  useEffect(() => {
    if (cids?.length) {
      setSelectedCidsList(cids)
    }
  }, [])

  return (
    <>
      <HeaderNew
        actualPageOnClick={() =>
          stateLocation?.isEditing ? history.push('resumo') : history.goBack()
        }
        actualPageTitle="Solicitar pedido ambulatorial"
      />

      <ContainerNew
        title="CID"
        titleColor="neutral800"
        titleSize="large"
        titleWeight="bold"
        subtitle="Inclua o CID referente à doença."
        primaryButton={
          <ButtonNew
            fullWidth
            size="large"
            onClick={handleSubmit}
            disabled={selectedCidsList.length === 0}
          >
            {stateLocation?.isEditing ? 'Salvar' : 'Próximo'}
          </ButtonNew>
        }
      >
        <S.Wrapper>
          <S.FormWrapper onSubmit={formik.handleSubmit}>
            <AutoCompleteNew
              name="cid"
              placeholder="Digite o código ou nome do CID"
              onInputChange={setCidSearchTerm}
              returnFullSuggestion
              labelOverlapping
              label="CID "
              labelSize="xxsmall"
              iconPosition="insideLeft"
              icon={<SearchIcon />}
              minCharactersSearch={3}
              suggestions={cidsList || []}
              onSuggestionClick={(cid) => selectCid(cid)}
              value={cidSearchTerm}
              onType={getCids}
              debounceDelay={800}
              disabled={!!selectedCid}
              clear={clearSelection}
              required
              requiredColor="neutral800"
            />

            <ButtonNew
              type="submit"
              size="large"
              outlined
              disabled={!selectedCid}
            >
              Incluir CID
            </ButtonNew>
          </S.FormWrapper>

          <DividerNew marginTop="24px" marginBottom="24px" />

          <HeadingNew
            as="h6"
            weight="semiBold"
            color="neutral600"
            size="medium"
            style={{ marginBottom: '24px' }}
          >
            CID incluídos
          </HeadingNew>

          {selectedCidsList.length ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                paddingBottom: '24px'
              }}
            >
              {selectedCidsList.map((cid: Cid) => {
                return (
                  <CidCard removeCid={removeCid} key={cid.code} cid={cid} />
                )
              })}
            </div>
          ) : (
            <>
              <CardNew
                padding="24px"
                background="neutral50"
                shadow="none"
                border="neutral200"
              >
                <SupportTextNew color="neutral400" size="small" weight="bold">
                  Você ainda não incluiu nenhum CID
                </SupportTextNew>
              </CardNew>
            </>
          )}
        </S.Wrapper>
      </ContainerNew>
    </>
  )
}

export default AmbulatoryCidsLayout
